import cx from 'classnames';
import { Pause, Play, RobotPlayStep, RobotRefresh, StopRounded } from 'education-icons';
import React, { ButtonHTMLAttributes, ComponentProps, ComponentType, SVGProps } from 'react';
import { AnyAction } from 'redux';
import { useIntl } from 'react-intl';

import { IconPause, IconPlay, IconStop, IconRepeat } from '@edu-frontend/modules/src/components/ui/Icon';
import { IconProps } from '@edu-frontend/modules/src/components/ui/Icon/lib/Icon.types';
import { Text } from '@edu-frontend/modules/src/components/ui/Text';
import { RobotContext, useRobotSelector } from '../context';
import { ROBOT_RUN_STATUS } from '../robot.constants';
import { ROBOT_TABS } from '../robot.types';
import { actions } from '../useRobotRun';
import { useRobotState } from '../robotState';
import { useMobileRobotViewAvailability } from '../../../utils/hooks/useMobileRobotViewAvailability';
import { HRDSButton } from '../../button/hrdsButton';
import { IconPlayStep } from './IconPlayStep';

// @ts-ignore
import styles from './robot__controls.module.scss';

interface Props {
    dispatch: React.Dispatch<AnyAction>;
    onControlsClick?: RobotContext['onControlsClick'];
    status: ROBOT_RUN_STATUS;
    speed: number[];
    setCurrentTab?: RobotContext['setCurrentTab'];
}

const Controls = ({ dispatch, onControlsClick, status, speed, setCurrentTab }: Props) => {
    const intl = useIntl();
    const { setMobileTab } = useRobotState();
    const mobileRobotViewIsActive = useMobileRobotViewAvailability(useRobotSelector((context) => context.problem));

    const Button = (
        props: Omit<ComponentProps<typeof HRDSButton>, 'icon'> &
            ButtonHTMLAttributes<HTMLButtonElement> & { Icon?: ComponentType<IconProps | SVGProps<SVGSVGElement>> }
    ) => {
        const { Icon, children, ...restProps } = props;
        return mobileRobotViewIsActive ? (
            <HRDSButton
                {...restProps}
                icon={Icon ? (className) => <Icon className={className} size="m" /> : undefined}
                rounded
                size="m"
            >
                {children}
            </HRDSButton>
        ) : (
            <button {...restProps}>{Icon ? <Icon /> : children}</button>
        );
    };

    const commonButtonClasses = cx(styles.button, !mobileRobotViewIsActive && styles.button_view_desktop);

    return (
        <div
            className={cx(
                styles.controls,
                !mobileRobotViewIsActive && styles.controls_view_desktop,
                { [styles.loading]: status === ROBOT_RUN_STATUS.LOADING },
                'qa_robot-controls'
            )}
        >
            {(() => {
                const className = cx(commonButtonClasses, !mobileRobotViewIsActive && styles.button_play);
                switch (status) {
                    case ROBOT_RUN_STATUS.FINISH:
                        return (
                            <Button
                                Icon={mobileRobotViewIsActive ? IconRepeat : RobotRefresh}
                                aria-label={intl.formatMessage({ id: 'a11y.keyboard.refreshButton' })}
                                className={cx(className, 'qa_robot-refresh-button')}
                                onClick={() => {
                                    dispatch(actions.stop());
                                    onControlsClick?.('reload');
                                }}
                                type="button"
                                view="primary"
                            />
                        );
                    case ROBOT_RUN_STATUS.PLAY:
                        return (
                            <Button
                                Icon={mobileRobotViewIsActive ? IconPause : Pause}
                                aria-label={intl.formatMessage({ id: 'a11y.keyboard.pauseButton' })}
                                className={cx(className, 'qa_robot-pause-button')}
                                onClick={() => {
                                    dispatch(actions.pause());
                                    onControlsClick?.('pause');
                                }}
                                type="button"
                                view="primary"
                            />
                        );
                    default:
                        return (
                            <Button
                                Icon={mobileRobotViewIsActive ? IconPlay : Play}
                                aria-label={intl.formatMessage({ id: 'a11y.keyboard.playButton' })}
                                className={cx(className, 'qa_robot-play-button')}
                                onClick={() => {
                                    dispatch(actions.play());
                                    setCurrentTab?.(ROBOT_TABS.CODE);
                                    setMobileTab('robot');
                                    onControlsClick?.('play');
                                }}
                                type="button"
                                view="primary"
                            />
                        );
                }
            })()}
            <Button
                Icon={mobileRobotViewIsActive ? IconPlayStep : RobotPlayStep}
                aria-label={intl.formatMessage({ id: 'a11y.keyboard.playStepButton' })}
                className={cx(commonButtonClasses, 'qa_robot-step-button')}
                onClick={() => {
                    dispatch(actions.step());
                    onControlsClick?.('step');
                }}
                type="button"
                view="outline"
            />
            <Button
                Icon={mobileRobotViewIsActive ? IconStop : StopRounded}
                aria-label={intl.formatMessage({ id: 'a11y.keyboard.stopButton' })}
                className={cx(commonButtonClasses, 'qa_robot-stop-button')}
                onClick={() => {
                    dispatch(actions.stop());
                    onControlsClick?.('stop');
                }}
                type="button"
                view="outline"
            />
            <Button
                aria-label={intl.formatMessage(
                    { id: 'a11y.keyboard.changeSpeedButton' },
                    { currentSpeed: speed[0], nextSpeed: speed[1] }
                )}
                className={cx(commonButtonClasses, styles.button_speed, 'qa_robot-shift-button')}
                onClick={() => {
                    dispatch(actions.shiftSpeed());
                    onControlsClick?.('shift');
                }}
                type="button"
                view="outline"
            >
                {mobileRobotViewIsActive ? (
                    <Text className={styles.speedButtonText} color="primary" typography="bodyS" weight="medium">
                        {speed[0]}
                        <small>&times;</small>
                    </Text>
                ) : (
                    <>
                        {speed[0]}
                        <small>&times;</small>
                    </>
                )}
            </Button>
        </div>
    );
};

export const ControlsMemo = React.memo(Controls);

const RobotControls = () => {
    return (
        <ControlsMemo
            dispatch={useRobotSelector((s) => s.dispatch)}
            onControlsClick={useRobotSelector((s) => s.onControlsClick)}
            setCurrentTab={useRobotSelector((s) => s.setCurrentTab)}
            speed={useRobotSelector((s) => s.state.speed)}
            status={useRobotSelector((s) => s.state.status)}
        />
    );
};

export default RobotControls;
